import { initializeApp } from 'firebase/app';
import { getStorage } from 'firebase/storage';
import { 
  initializeFirestore,
  writeBatch, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  serverTimestamp, 
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { 
  getAuth, 
  GoogleAuthProvider,
  EmailAuthProvider,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  createUserWithEmailAndPassword as firebaseCreateUserWithEmailAndPassword,
  signInAnonymously as firebaseSignInAnonymously,
  updateProfile,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  getRedirectResult,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { defaultUserPreferences } from '../types/user';
import type { User as FirebaseUser } from 'firebase/auth';
import { getMessaging } from 'firebase/messaging';
import { appAccessService } from '../services/appAccessService';

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Get the correct auth domain based on environment
const getAuthDomain = () => {
  if (window.location.hostname === 'localhost') {
    return 'plnnr-11ee8.firebaseapp.com';
  }
  return 'plnnr-11ee8.firebaseapp.com'; // Use Firebase project domain even in production
};

// Check if we're in a development/testing environment
const isTestingEnvironment = window.location.hostname === 'localhost' || 
                            window.location.hostname.includes('127.0.0.1') ||
                            window.location.hostname.includes('preview') ||
                            import.meta.env.MODE === 'development' ||
                            import.meta.env.MODE === 'testing' ||
                            import.meta.env.VITE_IS_TESTING === 'true';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: getAuthDomain(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // Only include measurementId in production to prevent Analytics auto-initialization
  ...(isTestingEnvironment ? {} : { measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID })
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const storage = getStorage(app);

// Set persistence to LOCAL
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting auth persistence:', error);
});

// Configure transport before any call can create the default Firestore instance.
// Force long polling because Safari beta releases can fail Firestore's normal
// streaming transport without triggering the SDK's automatic fallback.
const db = initializeFirestore(app, {
  experimentalForceLongPolling: true
});

// Initialize messaging
const messaging = getMessaging(app);

export { 
  db, 
  messaging,
  writeBatch, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  serverTimestamp, 
  getDoc, 
  updateDoc 
};

// Update connection state monitoring
const monitorFirestoreConnection = () => {
  let isOnline = true;

  window.addEventListener('online', () => {
    if (!isOnline) {
      isOnline = true;
    }
  });

  window.addEventListener('offline', () => {
    isOnline = false;
  });
};

// Initialize everything
const initializeFirebase = () => {
  monitorFirestoreConnection();
};

// Call initialization
initializeFirebase();

// Update the anonymous sign-in function
export const signInAnonymously = async () => {
  try {
    appAccessService.requirePrivateAppAccess({ isAnonymous: true });
    const result = await firebaseSignInAnonymously(auth);
    if (result.user) {
      // Set default display name and photo URL for anonymous users
      await updateProfile(result.user, {
        displayName: 'Guest User',
        photoURL: '/icons/default-avatar.png'
      });

      // Create user profile with proper null handling
      const userProfile = {
        id: result.user.uid,
        name: 'Guest User',
        email: null,
        photoURL: '/icons/default-avatar.png',
        isAnonymous: true,
        createdAt: serverTimestamp(),
        preferences: defaultUserPreferences
      };

      console.log('Creating anonymous user profile:', userProfile);

      // Create the user document
      await setDoc(doc(db, 'users', result.user.uid), userProfile);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Anonymous sign-in error:', error);
    throw error;
  }
};

// Add email link authentication settings
const actionCodeSettings = {
  url: window.location.origin + '/login/email-link',
  handleCodeInApp: true,
};

// Add new function for passwordless email sign-in
export const sendLoginLink = async (email: string) => {
  try {
    appAccessService.requirePrivateAppAccess({ email });
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save the email for later use
    window.localStorage.setItem('emailForSignIn', email);
    return true;
  } catch (error) {
    console.error('Error sending sign-in link:', error);
    throw error;
  }
};

// Add function to complete sign-in with email link
export const completeEmailSignIn = async (email: string, link: string) => {
  try {
    const result = await signInWithEmailLink(auth, email, link);
    window.localStorage.removeItem('emailForSignIn');

    try {
      appAccessService.requirePrivateAppAccess(result.user);
    } catch (error) {
      await signOut(auth);
      throw error;
    }

    await createUserProfile(result.user);
    
    return result;
  } catch (error) {
    console.error('Error completing email link sign-in:', error);
    throw error;
  }
};

// Add function to check if current URL is a sign-in link
export const isEmailSignInLink = (link: string) => {
  return isSignInWithEmailLink(auth, link);
};

// Configure Google provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('profile');
googleProvider.addScope('email');
googleProvider.setCustomParameters({
  prompt: 'select_account',
  redirect_uri: `${getAuthDomain()}/__/auth/handler`
});

// Google sign-in function
export const signInWithGoogle = async () => {
  try {
    auth.useDeviceLanguage();
    const result = await signInWithPopup(auth, googleProvider);
    try {
      appAccessService.requirePrivateAppAccess(result.user);
    } catch (error) {
      await signOut(auth);
      throw error;
    }
    return result;
  } catch (error: unknown) {
    console.error('[Firebase] Google sign in error:', error);
    throw error;
  }
};

export const signInWithEmailAndPassword = async (email: string, password: string) => {
  const result = await firebaseSignInWithEmailAndPassword(auth, email, password);
  try {
    appAccessService.requirePrivateAppAccess(result.user);
  } catch (error) {
    await signOut(auth);
    throw error;
  }
  return result;
};

export const createUserWithEmailAndPassword = async (email: string, password: string) => {
  if (!appAccessService.isReviewAccessEnabled()) {
    throw new Error('Public account creation is not available yet.');
  }
  return firebaseCreateUserWithEmailAndPassword(auth, email, password);
};

// Export all auth-related functions and classes
export { 
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  getRedirectResult,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  auth,
  storage
};

export const deleteUserData = async (userId: string) => {
  const MAX_BATCH_SIZE = 450;
  const refsToDelete: Array<ReturnType<typeof doc>> = [];

  const collectCollectionRefs = async (collectionName: string) => {
    const snapshot = await getDocs(
      query(collection(db, collectionName), where('userId', '==', userId))
    );
    snapshot.forEach((snapshotDoc) => refsToDelete.push(snapshotDoc.ref));
  };

  // Top-level user-scoped collections
  const userScopedCollections = [
    'captures',
    'contextRecords',
    'contextRelations',
    'plans',
    'actions',
    'reviewItems',
    'automationReceipts',
    'signals'
  ];

  await Promise.all(userScopedCollections.map((collectionName) => collectCollectionRefs(collectionName)));

  // Finally remove the user document itself.
  refsToDelete.push(doc(db, 'users', userId));

  for (let index = 0; index < refsToDelete.length; index += MAX_BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = refsToDelete.slice(index, index + MAX_BATCH_SIZE);
    chunk.forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
};

// Update the createUserProfile function
export const createUserProfile = async (user: FirebaseUser, displayName?: string) => {
  try {
    const reference = doc(db, 'users', user.uid)
    const existing = await getDoc(reference)
    if (existing.exists()) return existing.data()

    const userProfile = {
      id: user.uid,
      name: displayName || user.displayName || user.email?.split('@')[0] || 'User',
      email: user.email,
      photoURL: user.photoURL || '/icons/default-avatar.png',
      isAnonymous: false,
      emailVerified: user.emailVerified,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Add new fields with default values
      lastLogin: serverTimestamp(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language.split('-')[0], // Get browser language
      accountStatus: 'active',
      bio: '',
      location: '',
      website: '',
      preferences: defaultUserPreferences,
    };

    await setDoc(reference, userProfile);
    
    if (!user.displayName) {
      await updateProfile(user, {
        displayName: userProfile.name
      });
    }

    return userProfile;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

// Export the app instance as well
export { app };
